<template>
    <!-- p-l-0/p-r-0: wiersze towarow maja zaczynac sie w tej samej linii co pola
         pozostalych sekcji formularza - bez tego dodatkowe zagniezdzenie kolumn
         przesuwalo cala sekcje o szerokosc odstepu kolumny w prawo -->
    <div class="col-12 p-l-0 p-r-0">
        <ItemRow
            v-for="(item, index) in items"
            :key="index"
            :canDeleteItem="canDeleteItem"
            :disabled="disabled"
            :errors="errors"
            :item="item"
            :itemIndex="index"
            :additionalFields="additionalFields"
            :gtus="gtus"
            :procedures="procedures"
            :requiredFields="requiredFields"
            :unitPriceCalculation="unitPriceCalculation"
            :permissionsAccess="permissionsAccess"
            :vatRates="vatRates"
            :isSymbolVisible="isSymbolVisible"
            :standardItems="standardItems"
            @inputs="$emit('itemInput', {index, ...$event})"
            @itemChanged="emitSummaries"
            @addStandardItem="addStandardItem"
            @removeItem="$emit('removeItem', index)"
            @selectedVatRate="handleSelectedVatRate(index, $event)"
        />

        <hr>

        <RoundingRow :roundings="roundings"/>

        <SummaryRow
            :disabled="disabled"
            :errors="errors"
            :sums="summaries"
            @addNewItem="$emit('addNewItem')"
        />
    </div>
</template>

<script>
import RoundingRow from "./RoundingRow";
import SummaryRow from "./SummaryRow";
import ItemRow from "../default/ItemRow";
import hasErrors from "../../../../mixins/hasErrors";
import inputEmit from "../../../../mixins/inputEmit";
import hasVatRates from "../../../../mixins/hasVatRates";
import {forEach, groupBy, round} from "lodash";
import hasRequiredFields from "../../../../mixins/hasRequiredFields";
import hasPermissions from "../../../../mixins/hasPermissions";

export default {
    name: "ItemsSection",
    components: {ItemRow, SummaryRow, RoundingRow},
    mixins: [hasErrors, inputEmit, hasVatRates, hasRequiredFields, hasPermissions],
    props: {
        items: {
            type: Array,
            required: true,
        },
        unitPriceCalculation: {
            type: Boolean,
            required: true,
        },

        disabled: {
            type: Boolean,
            default: false,
        },
        isSymbolVisible: {
            type: Number,
            default: 0,
        },
        standardItems: {
            type: Array,
            default: [],
        },
        additionalFields: {
            type: Array,
            default: () => [],
        },
        gtus: {
            type: Array,
            default: () => [],
        },
        procedures: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            roundings: {
                gross: 0,
                net: 0,
                vat: 0,
            },
            summaries: {
                gross: 0,
                net: 0,
                vat: 0,
            },
        };
    },
    computed: {
        groupedByVatRate() {
            return groupBy(this.items, item => item.vat_rate);
        },
        canDeleteItem() {
            return this.items.length > 1;
        },
        sumItemsGrossAmount() {
            let sum = 0;
            if (this.unitPriceCalculation) {
                sum = this.getSum('gross_amount');
            } else {
                const groupedItems = this.groupedByVatRate;
                Object.keys(groupedItems).forEach((key) => {
                    const groupSum = groupedItems[key].reduce((prevVal, item) => prevVal + item.net_amount, 0);
                    const vatRateAmount = this.getVatRateAmount(parseInt(key));

                    sum += round(round(((1 + vatRateAmount) * groupSum), 10), 2);
                });
            }

            return this.parseMoney(sum);
        },
        sumItemsNetAmount() {
            let sum = 0;
            if (this.unitPriceCalculation) {
                sum = this.sumItemsGrossAmount - this.sumItemsVatAmount;
            } else {
                sum = this.getSum('net_amount');
            }

            return this.parseMoney(sum);
        },
        sumItemsVatAmount() {
            let sum = 0;
            if (this.unitPriceCalculation) {
                const groupedItems = this.groupedByVatRate;
                Object.keys(groupedItems).forEach((key) => {
                    const groupSum = groupedItems[key].reduce((prevVal, item) => prevVal + item.gross_amount, 0);
                    const vatRateAmount = this.getVatRateAmount(parseInt(key));

                    sum += round(round(((vatRateAmount / (1 + vatRateAmount)) * groupSum), 10), 2);
                });
            } else {
                sum = this.sumItemsGrossAmount - this.sumItemsNetAmount;
            }

            return this.parseMoney(sum);
        },
        roundingsNet() {
            return this.rounding('net');
        },
        roundingsVat() {
            return this.rounding('vat');
        },
        roundingsGross() {
            return this.rounding('gross');
        },
    },
    methods: {
        addStandardItem(index){
            this.$emit('addStandardItem', index)
        },

        getSum(field) {
            return this.items.reduce((prevVal, item) => prevVal + item[field], 0);
        },
        parseMoney(number) {
            number = this.$options.filters.money(number);
            return this.$options.filters.floatMoney(number);
        },
        calculateSummary() {
            this.summaries.gross = this.sumItemsGrossAmount;
            this.summaries.net = this.sumItemsNetAmount;
            this.summaries.vat = this.sumItemsVatAmount;
        },
        emitSummaries() {
            this.calculateSummary();
            this.calculateRoundings();
            this.emitInput('net_amount', this.summaries.net);
            this.emitInput('gross_amount', this.summaries.gross);
            this.emitInput('vat_amount', this.summaries.vat);
        },
        calculateRoundings() {
            this.roundings.gross = this.roundingsGross;
            this.roundings.net = this.roundingsNet;
            this.roundings.vat = this.roundingsVat;
        },
        rounding(field) {
            const itemsRowSum = this.getSum(`${field}_amount`);
            const itemComputedSum = this.summaries[field];
            let result = itemComputedSum - itemsRowSum;
            result = Math.round(result * 100) / 100;

            return result;
        },
        handleSelectedVatRate(index, vatRateId) {
            const vatRate = this.getVatRate(vatRateId);
            if (vatRate.status === 'exempt') {
                this.$emit('hasExemptVat');
            }
            this.$emit('itemInput', {index, name: 'vat_rate', value: vatRateId});
        },
        initSummaries(summaries) {
            forEach(summaries, (value, name) => this.summaries[name] = value);
        },
        sumVatAmountAndEmitSummaries() {
            this.summaries.vat = this.sumItemsVatAmount;
            this.emitSummaries(false);
        },
    },
};
</script>

